import React, { useMemo, useState, useEffect } from "react";
import "./InsumosProduccionView.css";
import {
  getRequisicionActual,
  saveStockCapturas,
  createRequisicionAutomatica,
  getInsumosApiBaseUrl,
} from "../../services/insumosService.js";
import { exportInsumosProveedorPdf } from "../../utils/exportInsumosProveedorPdf.js";
import { cargarNombresOficialesStock } from "./components/stockCatalogoOficial";

import StockCaptureModal from "./components/StockCaptureModal.jsx";
import { oficinasApi, municipiosApi } from "../../api/catalogosApi";
import PendientesCatalogoInsumos from "./components/PendientesCatalogoInsumos.jsx";
import StockHistorialModal from "./components/StockHistorialModal.jsx";
import { getUserGroups } from "../../auth/permissions.js";
const LEAD_TIME_DAYS = 4;
const SAFETY_PERCENT = 0;

const STOCK_TRACEABILITY = {
  ultimaCaptura: "29/jun/2026 12:45 PM",
  responsable: "Supervisor operativo",
  oficinasActualizadas: 7,
  oficinasTotales: 13,
  origen: "Captura operativa",
};

const PRINTER_RULES = {
  IDP_SMART_51L: {
    label: "SMART 51L",
    tarjetasPorPaquete: 500,
    licenciasPorRibbon: 250,
    laminacionesPorLaminador: 320,
    laminacionesPorLicencia: 2,
    requiereRetransfer: false,
    licenciasPorRetransfer: 0,
    minRibbon: 3,
    minLaminador: 3,
    minRetransfer: 0,
    minRollosTerminal: 17,
    equivalencias: [
      { modelo: "SMART 51L", insumo: "Tarjetas Preimpresas", texto: "1 paquete = 500 tarjetas" },
      { modelo: "SMART 51L", insumo: "Ribbon", texto: "1 ribbon = 250 licencias" },
      { modelo: "SMART 51L", insumo: "Laminador", texto: "1 laminador = 320 laminaciones / 160 licencias" },
      { modelo: "SMART 51L", insumo: "Retransfer", texto: "No aplica" },
      { modelo: "Todos", insumo: "Rollo térmico terminal", texto: "Mínimo operativo: 17 rollos" },
    ],
  },

  K30_SWITF_PRO: {
    label: "K30 SWITF PRO",
    tarjetasPorPaquete: 500,
    licenciasPorRibbon: 500,
    laminacionesPorLaminador: 1000,
    laminacionesPorLicencia: 2,
    requiereRetransfer: true,
    licenciasPorRetransfer: 500,
    minRibbon: 3,
    minLaminador: 3,
    minRetransfer: 3,
    minRollosTerminal: 17,
    equivalencias: [
      { modelo: "K30 SWITF PRO", insumo: "Tarjetas Preimpresas", texto: "1 paquete = 500 tarjetas" },
      { modelo: "K30 SWITF PRO", insumo: "Ribbon", texto: "1 ribbon = 500 licencias" },
      { modelo: "K30 SWITF PRO", insumo: "Laminador", texto: "1 laminador = 1000 laminaciones / 500 licencias" },
      { modelo: "K30 SWITF PRO", insumo: "Retransfer", texto: "1 retransfer = 500 licencias" },
      { modelo: "Todos", insumo: "Rollo térmico terminal", texto: "Mínimo operativo: 17 rollos" },
    ],
  },
};
function normalizeInsumosText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function isK30InsumosOffice(row) {
  const oficinaId = normalizeInsumosText(row?.oficinaId || row?.id);
  const oficina = normalizeInsumosText(row?.oficina || row?.nombre);
  const municipio = normalizeInsumosText(row?.municipio);

  const isOthonPBlanco =
    oficinaId.includes("OTHON") ||
    oficina.includes("OTHON") ||
    oficina.includes("P BLANCO") ||
    municipio.includes("OTHON") ||
    municipio.includes("P BLANCO");

  const isTransitoCancunBenitoJuarez =
    oficinaId.includes("BENITO_TRANSITO") ||
    oficinaId.includes("BENITO_JUAREZ_TRANSITO") ||
    oficinaId.includes("TRANSITO_CANCUN") ||
    oficina.includes("BENITO JUAREZ TRANSITO") ||
    oficina.includes("TRANSITO CANCUN") ||
    oficina.includes("CANCUN TRANSITO") ||
    (municipio.includes("BENITO JUAREZ") && oficina.includes("TRANSITO"));

  return isOthonPBlanco || isTransitoCancunBenitoJuarez;
}

function resolveInsumosPrinterKey(row) {
  const raw = normalizeInsumosText(row?.impresora || row?.modelo || row?.printerLabel);

  if (raw.includes("K30")) {
    return "K30_SWITF_PRO";
  }

  if (isK30InsumosOffice(row)) {
    return "K30_SWITF_PRO";
  }

  return "IDP_SMART_51L";
}

function normalizeInsumosStock(row) {
  const stock = row?.stock || {};
  const printerKey = resolveInsumosPrinterKey(row);
  const isK30 = printerKey === "K30_SWITF_PRO";

  return {
    plasticos: numberOrZeroPreserveZero(stock.plasticos ?? row?.plasticos),
    ribbon: numberOrZeroPreserveZero(stock.ribbon ?? row?.ribbon),
    laminador: numberOrZeroPreserveZero(stock.laminador ?? row?.laminador),
    retransfer: isK30 ? numberOrZeroPreserveZero(stock.retransfer ?? row?.retransfer) : 0,
    rollos: numberOrZeroPreserveZero(stock.rollos ?? row?.rollos),
  };
}

const oficinasSeed = [
  { oficinaId: "ISLA_MUJERES_INSULAR", municipio: "Isla Mujeres", oficina: "Isla Mujeres Insular", impresora: "IDP_SMART_51L", promedioMensual: 531, stock: { plasticos: 100, ribbon: 0, laminador: 1, retransfer: 0, rollos: 9 } },
  { oficinaId: "LAZARO_CARDENAS", municipio: "Lázaro Cárdenas", oficina: "Lázaro Cárdenas", impresora: "IDP_SMART_51L", promedioMensual: 573, stock: { plasticos: 0, ribbon: 0, laminador: 0, retransfer: 0, rollos: 0 } },
  { oficinaId: "PUERTO_MORELOS", municipio: "Puerto Morelos", oficina: "Puerto Morelos", impresora: "IDP_SMART_51L", promedioMensual: 999, stock: { plasticos: 0, ribbon: 0, laminador: 0, retransfer: 0, rollos: 0 } },
  { oficinaId: "UNIDAD_MOVIL_1", municipio: "Puerto Morelos", oficina: "Unidad Móvil 1", impresora: "IDP_SMART_51L", promedioMensual: 473, stock: { plasticos: 0, ribbon: 0, laminador: 0, retransfer: 0, rollos: 7 } },
  { oficinaId: "TULUM", municipio: "Tulum", oficina: "Tulum Central", impresora: "IDP_SMART_51L", promedioMensual: 1004, stock: { plasticos: 975, ribbon: 1, laminador: 2, retransfer: 0, rollos: 28 } },
  { oficinaId: "BENITO_TAXISTAS", municipio: "Benito Juárez", oficina: "Benito Juárez Taxistas", impresora: "IDP_SMART_51L", promedioMensual: 4441, stock: { plasticos: 3400, ribbon: 5, laminador: 14, retransfer: 0, rollos: 60 } },
  { oficinaId: "OTHON_BLANCO", municipio: "Othón P. Blanco", oficina: "Othón P. Blanco", impresora: "K30_SWITF_PRO", promedioMensual: 2612, stock: { plasticos: 1500, ribbon: 3, laminador: 5, retransfer: 3, rollos: 10 } },
];

function fmt(value) {
  return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function dec(value) {
  return new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number(value || 0));
}

function labelInsumo(value) {
  const map = {
    plasticos: "Tarjetas Preimpresas",
    ribbon: "Ribbon",
    laminador: "Laminador",
    retransfer: "Retransfer",
    rollos: "Rollo térmico para terminal",
  };

  return map[value] || value || "N/D";
}

function estadoLabel(value) {
  const map = {
    VENCIDA: "VENCIDA",
    HOY: "PEDIR HOY",
    PREVENTIVA: "PREVENTIVA",
    OK: "OK",
  };

  return map[value] || value || "N/D";
}

function estadoClass(value) {
  return "req-badge req-" + String(value || "").toLowerCase();
}

function severity(value) {
  const map = {
    VENCIDA: 4,
    HOY: 3,
    PREVENTIVA: 2,
    OK: 1,
  };

  return map[value] || 0;
}

function calcularPedido(row) {
  const printerKey = resolveInsumosPrinterKey(row);
  const reglas = PRINTER_RULES[printerKey] || PRINTER_RULES.IDP_SMART_51L;
  const stock = normalizeInsumosStock(row);
  const deficit = Math.max(0, Number(row.deficit || 0));

  const pedidoRollos = Math.max(
    0,
    Number(reglas.minRollosTerminal || 17) - Number(stock.rollos || 0)
  );

  if (!deficit) {
    return {
      plasticos: 0,
      ribbon: 0,
      laminador: 0,
      retransfer: 0,
      rollos: pedidoRollos,
    };
  }

  return {
    plasticos: Math.ceil(deficit / reglas.tarjetasPorPaquete) * reglas.tarjetasPorPaquete,
    ribbon: Math.ceil(deficit / reglas.licenciasPorRibbon),
    laminador: Math.ceil(
      (deficit * reglas.laminacionesPorLicencia) / reglas.laminacionesPorLaminador
    ),
    retransfer: reglas.requiereRetransfer
      ? Math.ceil(deficit / reglas.licenciasPorRetransfer)
      : 0,
    rollos: pedidoRollos,
  };
}

function calcularOficina(oficina) {
  const printerKey = resolveInsumosPrinterKey(oficina);
  const reglas = PRINTER_RULES[printerKey] || PRINTER_RULES.IDP_SMART_51L;
  const stock = normalizeInsumosStock(oficina);

  const plasticos = Number(stock.plasticos || 0);
  const ribbon = Number(stock.ribbon || 0);
  const laminador = Number(stock.laminador || 0);
  const retransfer = reglas.requiereRetransfer ? Number(stock.retransfer || 0) : 0;
  const rollos = Number(stock.rollos || 0);

  const capacidadPlasticos = plasticos;
  const capacidadRibbon = ribbon * reglas.licenciasPorRibbon;
  const capacidadLaminador = Math.floor(
    (laminador * reglas.laminacionesPorLaminador) / reglas.laminacionesPorLicencia
  );
  const capacidadRetransfer = reglas.requiereRetransfer
    ? retransfer * reglas.licenciasPorRetransfer
    : Infinity;

  const capacidadReal = Math.min(
    capacidadPlasticos,
    capacidadRibbon,
    capacidadLaminador,
    capacidadRetransfer
  );

  const promedioMensual = Number(oficina.promedioMensual || 0);
  const consumoDiario = promedioMensual / 30;
  const diasCobertura = consumoDiario > 0 ? capacidadReal / consumoDiario : 0;

  const colchon = 0;
  const consumoEntrega = Math.ceil(consumoDiario * LEAD_TIME_DAYS);
  const puntoPedido = Math.ceil(consumoEntrega + colchon);
  const stockObjetivo = Math.ceil(promedioMensual + colchon + consumoEntrega);
  const deficit = Math.max(0, stockObjetivo - capacidadReal);

  const minimosOperativos = [];

  if (ribbon < reglas.minRibbon) minimosOperativos.push("ribbon");
  if (laminador < reglas.minLaminador) minimosOperativos.push("laminador");
  if (reglas.requiereRetransfer && retransfer < reglas.minRetransfer) minimosOperativos.push("retransfer");
  if (rollos < reglas.minRollosTerminal) minimosOperativos.push("rollos");

  const capacidades = {
    plasticos: capacidadPlasticos,
    ribbon: capacidadRibbon,
    laminador: capacidadLaminador,
    retransfer: reglas.requiereRetransfer ? capacidadRetransfer : null,
  };

  let insumoLimitante = Object.entries(capacidades)
    .filter(function(entry) {
      return entry[1] !== null;
    })
    .sort(function(a, b) {
      return a[1] - b[1];
    })[0][0];

  if (minimosOperativos.includes("rollos")) {
    insumoLimitante = "rollos";
  }

  const diasAtraso = Math.max(0, Math.ceil(LEAD_TIME_DAYS - diasCobertura));
  const diasParaPedir = Math.max(0, Math.floor(diasCobertura - LEAD_TIME_DAYS));

  let estado = "OK";

  if (minimosOperativos.length > 0 || diasAtraso > 0) {
    estado = "VENCIDA";
  } else if (diasParaPedir <= 0) {
    estado = "HOY";
  } else if (capacidadReal <= puntoPedido) {
    estado = "PREVENTIVA";
  }

  const base = {
    ...oficina,
    impresora: printerKey,
    modelo: printerKey,
    printerLabel: reglas.label,
    stock: stock,
    reglas: reglas,
    capacidades: capacidades,
    capacidadReal: capacidadReal,
    consumoDiario: consumoDiario,
    diasCobertura: Number(diasCobertura.toFixed(1)),
    colchon: colchon,
    consumoEntrega: consumoEntrega,
    puntoPedido: puntoPedido,
    stockObjetivo: stockObjetivo,
    deficit: deficit,
    insumoLimitante: insumoLimitante,
    minimosOperativos: minimosOperativos,
    motivoOperativo: minimosOperativos.length
      ? "Mínimo operativo bajo: " + minimosOperativos.map(labelInsumo).join(", ")
      : null,
    diasAtraso: diasAtraso,
    diasParaPedir: diasParaPedir,
    fechaLimite: diasAtraso > 0 ? "Vencida" : "En tiempo",
    estado: estado,
    debePedir: ["VENCIDA", "HOY", "PREVENTIVA"].includes(estado),
  };

  return {
    ...base,
    pedidoSugerido: calcularPedido(base),
  };
}

function narrativa(row) {
  const minimos = Array.isArray(row.minimosOperativos) ? row.minimosOperativos : [];
  const minimosLabel = minimos.map(labelInsumo).join(", ");

  if (row.estado === "VENCIDA") {
    if (row.motivoOperativo) {
      return row.motivoOperativo;
    }

    if (minimos.length > 0) {
      return "Mínimo operativo bajo: " + minimosLabel;
    }

    if (Number(row.diasAtraso || 0) > 0) {
      return String(fmt(row.diasAtraso)) + " días de atraso";
    }

    return "Crítico por abasto operativo";
  }

  if (row.estado === "HOY") {
    if (row.motivoOperativo) {
      return row.motivoOperativo;
    }

    if (minimos.length > 0) {
      return "Mínimo operativo bajo: " + minimosLabel;
    }

    return "Fecha límite: " + String(row.fechaLimite || "En tiempo");
  }

  if (row.estado === "PREVENTIVA") {
    return "Programar requisición";
  }

  return "Abasto suficiente hoy";
}

function cantidadPrincipal(row) {
  const pedido = row.pedidoSugerido || row.pedido || {};
  const limitante = row.insumoLimitante;

  if (limitante === "plasticos") return Number(pedido.plasticos || 0);
  if (limitante === "ribbon") return Number(pedido.ribbon || 0);
  if (limitante === "laminador") return Number(pedido.laminador || 0);
  if (limitante === "retransfer") return Number(pedido.retransfer || 0);
  if (limitante === "rollos") return Number(pedido.rollos || 0);

  return (
    Number(pedido.plasticos || 0) ||
    Number(pedido.ribbon || 0) ||
    Number(pedido.laminador || 0) ||
    Number(pedido.retransfer || 0) ||
    Number(pedido.rollos || 0) ||
    0
  );
}


function getPedidoProveedor(row) {
  return row.pedidoSugerido || row.pedido || {};
}

function getStockActual(row, insumo) {
  const stock = row.stock || {};
  return Number(stock[insumo] ?? row[insumo] ?? 0);
}

function pedidoProveedorLineas(row) {
  const pedido = getPedidoProveedor(row);

  return [
    { key: "plasticos", label: "Tarjetas", value: Number(pedido.plasticos || 0) },
    { key: "ribbon", label: "Ribbon", value: Number(pedido.ribbon || 0) },
    { key: "laminador", label: "Laminador", value: Number(pedido.laminador || 0) },
    { key: "retransfer", label: "Retransfer", value: Number(pedido.retransfer || 0) },
    { key: "rollos", label: "Rollo térmico para terminal", value: Number(pedido.rollos || 0) },
  ].filter(function(item) {
    return item.value > 0;
  });
}

function tienePedidoProveedor(row) {
  return pedidoProveedorLineas(row).length > 0;
}

function kitProveedorMeta(row) {
  const pedido = getPedidoProveedor(row);
  const kits = Number(pedido.kits || 0);
  const demanda = Number(pedido.demandaObjetivo || 0);

  if (kits > 0 && demanda > 0) {
    return fmt(kits) + " kit(s) proveedor · demanda objetivo operativo " + fmt(demanda) + " licencias";
  }

  if (kits > 0) {
    return fmt(kits) + " kit(s) proveedor";
  }

  return "Kit proveedor calculado por backend";
}

function stockMinimoTexto(row) {
  const minimos = Array.isArray(row.minimosOperativos) ? row.minimosOperativos : [];

  if (!minimos.length) {
    return null;
  }

  return minimos
    .map(function(insumo) {
      return labelInsumo(insumo) + " actual " + fmt(getStockActual(row, insumo));
    })
    .join(" · ");
}


function getPedidoProveedorCompacto(row) {
  return row.pedidoSugerido || row.pedido || {};
}

function pedidoCompactoLineas(row) {
  const pedido = getPedidoProveedorCompacto(row);

  return [
    { key: "plasticos", label: "Tarjetas", value: Number(pedido.plasticos || 0) },
    { key: "ribbon", label: "Ribbon", value: Number(pedido.ribbon || 0) },
    { key: "laminador", label: "Laminador", value: Number(pedido.laminador || 0) },
    { key: "retransfer", label: "Retransfer", value: Number(pedido.retransfer || 0) },
    { key: "rollos", label: "Rollo térmico para terminal", value: Number(pedido.rollos || 0) },
  ].filter(function(item) {
    return item.value > 0;
  });
}

function causaOperativaCompacta(row) {
  const minimos = Array.isArray(row.minimosOperativos) ? row.minimosOperativos : [];

  if (minimos.length > 0) {
    return "Bajo mínimo: " + minimos.map(labelInsumo).join(", ");
  }

  if (row.estado === "VENCIDA" && Number(row.diasAtraso || 0) > 0) {
    return "Cobertura vencida: " + fmt(row.diasAtraso) + " días";
  }

  if (row.estado === "PREVENTIVA") {
    return "Preventiva por cobertura";
  }

  if (row.estado === "HOY") {
    return "Punto de pedido hoy";
  }

  return "Surtido por kit proveedor";
}

function kitMetaCompacta(row) {
  const pedido = getPedidoProveedorCompacto(row);
  const kits = Number(pedido.kits || 0);
  const demanda = Number(pedido.demandaObjetivo || 0);

  if (kits > 0 && demanda > 0) {
    return fmt(kits) + " kit(s) · objetivo operativo " + fmt(demanda);
  }

  if (kits > 0) {
    return fmt(kits) + " kit(s)";
  }

  return "Pedido sugerido";
}


function isComplementaria(row, variant) {
  return variant === "kit" || (row.estado === "OK" && tienePedidoProveedor(row));
}

function estadoVisualLabel(row, variant) {
  if (isComplementaria(row, variant)) return "COMPLEMENTARIA";
  return estadoLabel(row.estado);
}

function estadoVisualClass(row, variant) {
  if (isComplementaria(row, variant)) return "req-badge req-complementaria";
  return estadoClass(row.estado);
}

function causaVisualCompacta(row, variant) {
  if (isComplementaria(row, variant)) {
    return "Incluida por compra consolidada";
  }

  return causaOperativaCompacta(row);
}

function subcausaVisualCompacta(row, variant) {
  if (isComplementaria(row, variant)) {
    return "No crítica · kit cerrado / compra consolidada";
  }

  if (row.insumoLimitante) {
    return "Limitante: " + labelInsumo(row.insumoLimitante);
  }

  return "Kit proveedor";
}

function accionVisualTitulo(row, variant) {
  if (isComplementaria(row, variant)) return "PDF";
  if (row.estado === "PREVENTIVA") return "Obs.";
  return "PDF";
}

function accionVisualSubtitulo(row, variant) {
  if (isComplementaria(row, variant)) return "Complementaria";
  if (row.estado === "PREVENTIVA") return "Preventiva";
  return "Incluido";
}


function numberOrZeroPreserveZero(value) {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function readNumberPreserveZero(primary, fallback) {
  if (primary !== undefined && primary !== null && primary !== "") {
    return numberOrZeroPreserveZero(primary);
  }

  return numberOrZeroPreserveZero(fallback);
}
function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function isK30Office(row) {
  const oficinaId = normalizeText(row.oficinaId || row.id);
  const oficina = normalizeText(row.oficina || row.nombre);
  const municipio = normalizeText(row.municipio);

  const isOthonPBlanco =
    oficinaId.includes("OTHON") ||
    oficina.includes("OTHON") ||
    oficina.includes("P BLANCO") ||
    municipio.includes("OTHON") ||
    municipio.includes("P BLANCO");

  const isTransitoCancunBenitoJuarez =
    oficinaId.includes("BENITO_TRANSITO") ||
    oficinaId.includes("BENITO_JUAREZ_TRANSITO") ||
    oficinaId.includes("TRANSITO_CANCUN") ||
    oficina.includes("BENITO JUAREZ TRANSITO") ||
    oficina.includes("TRANSITO CANCUN") ||
    oficina.includes("CANCUN TRANSITO") ||
    (municipio.includes("BENITO JUAREZ") && oficina.includes("TRANSITO"));

  return isOthonPBlanco || isTransitoCancunBenitoJuarez;
}

function getOperationalPrinterModel(row) {
  return isK30Office(row) ? "K30_SWITF_PRO" : "IDP_SMART_51L";
}

function getOperationalPrinterLabel(row) {
  return isK30Office(row) ? "K30 SWITF PRO" : "SMART 51L";
}

function normalizeBackendInsumosRow(row) {
  const stock = row.stock || {};
  const pedidoSugerido = row.pedidoSugerido || row.pedido || {};
  const printerKey = resolveInsumosPrinterKey(row);
  const reglas = PRINTER_RULES[printerKey] || PRINTER_RULES.IDP_SMART_51L;
  const isK30Backend = printerKey === "K30_SWITF_PRO";
  const normalizedPedido = {
    plasticos: numberOrZeroPreserveZero(pedidoSugerido.plasticos),
    ribbon: numberOrZeroPreserveZero(pedidoSugerido.ribbon),
    laminador: numberOrZeroPreserveZero(pedidoSugerido.laminador),
    retransfer: isK30Backend ? numberOrZeroPreserveZero(pedidoSugerido.retransfer) : 0,
    rollos: numberOrZeroPreserveZero(pedidoSugerido.rollos),
    kits: numberOrZeroPreserveZero(pedidoSugerido.kits || row.kitProveedor?.kits),
    demandaObjetivo: numberOrZeroPreserveZero(pedidoSugerido.demandaObjetivo || row.kitProveedor?.demandaObjetivo),
  };

  return {
    ...row,
    id: row.id || row.oficinaId,
    oficinaId: row.oficinaId || row.id,
    nombre: row.nombre || row.oficina,
    oficina: row.oficina || row.nombre,
    municipio: row.municipio,
    modelo: printerKey,
    impresora: printerKey,
    printerLabel: reglas.label,
    promedioMensual: readNumberPreserveZero(row.promedioMensual, 0),
    stock: {
      plasticos: readNumberPreserveZero(stock.plasticos, row.plasticos),
      ribbon: readNumberPreserveZero(stock.ribbon, row.ribbon),
      laminador: readNumberPreserveZero(stock.laminador, row.laminador),
      retransfer: getOperationalPrinterModel(row) === "K30_SWITF_PRO" ? readNumberPreserveZero(stock.retransfer, row.retransfer) : 0,
      rollos: readNumberPreserveZero(stock.rollos, row.rollos),
    },
    pedidoSugerido: normalizedPedido,
    pedido: normalizedPedido,
    estado: row.estado || row.estadoCalculado || "OK",
    estadoCalculado: row.estadoCalculado || row.estado || "OK",
    insumoLimitante: row.insumoLimitante || "plasticos",
    ultimaCaptura: row.ultimaCaptura || null,
    responsable: row.responsable || null,
    minimosOperativos: row.minimosOperativos || [],
    motivoOperativo: row.motivoOperativo || null,
    kitProveedor: row.kitProveedor || null,
    debePedir: row.debePedir !== false,
    source: "BACKEND",
  };
}

function buildCapturesFromRows(rows) {
  return rows
    .filter((row) => row.oficinaId || row.id)
    .map((row) => {
      const stock = row.stock || {};

      return {
        oficinaId: row.oficinaId || row.id,
        ...(row.nuevaOficina ? {
          impresora: row.impresora,
          modelo: row.modelo,
          totalImpresoras: Number(row.totalImpresoras),
          nombreOficina: row.oficina,
          municipio: row.municipio
        } : {}),
        promedioMensual: readNumberPreserveZero(row.promedioMensual, 0),
        stock: {
          plasticos: readNumberPreserveZero(stock.plasticos, row.plasticos),
          ribbon: readNumberPreserveZero(stock.ribbon, row.ribbon),
          laminador: readNumberPreserveZero(stock.laminador, row.laminador),
          retransfer: getOperationalPrinterModel(row) === "K30_SWITF_PRO" ? readNumberPreserveZero(stock.retransfer, row.retransfer) : 0,
          rollos: readNumberPreserveZero(stock.rollos, row.rollos),
        },
      };
    });
}


const INSUMOS_EQUIVALENCIAS_OPERATIVAS = [
  {
    modelo: "SMART 51L",
    items: [
      { insumo: "Tarjetas Preimpresas", equivalencia: "1 paquete = 500 tarjetas" },
      { insumo: "Ribbon", equivalencia: "1 ribbon = 250 licencias" },
      { insumo: "Laminador", equivalencia: "1 laminador = 320 laminaciones = 160 licencias" },
      { insumo: "Retransfer", equivalencia: "No aplica" },
    ],
  },
  {
    modelo: "K30 SWITF PRO",
    items: [
      { insumo: "Tarjetas Preimpresas", equivalencia: "1 paquete = 500 tarjetas" },
      { insumo: "Ribbon", equivalencia: "1 ribbon = 500 licencias" },
      { insumo: "Laminador", equivalencia: "1 laminador = 1000 laminaciones = 500 licencias" },
      { insumo: "Retransfer", equivalencia: "1 retransfer = 500 licencias" },
    ],
  },
];
const IPD_BOOTSTRAP_OFICINAS = new Set([
  "OFI_2e79f3fc-6ff1-4c00-97d2-d41f1e36eb49",
  "OFI_fb44adb1-9603-4e25-a2ff-450cb882f9c8",
  "OFI_54a33014-ecf7-4272-ad67-1ad9e2161319",
  "OFI_49228c43-d8ec-4c5f-a185-617f08cd5e3f"
]);

function ipdListaCatalogo(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.oficinas)) return data.oficinas;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  return [];
}

function ipdHabilitadaInsumos(oficina) {
  const id = String(oficina.oficinaId || oficina.id || "");
  const modulos = Array.isArray(oficina.modulosHabilitados)
    ? oficina.modulosHabilitados
    : [];

  return IPD_BOOTSTRAP_OFICINAS.has(id) ||
    modulos.includes("INSUMOS_LICENCIAS");
}

export default function InsumosProduccionView() {
  const userGroups = getUserGroups();

  const isProvider =
    userGroups.includes("provider") ||
    userGroups.includes("proveedor");
  const [rows, setRows] = useState(oficinasSeed);
  const [captureRows, setCaptureRows] = useState(oficinasSeed);
  const [oficinasNuevasCatalogo, setOficinasNuevasCatalogo] = useState([]);

  useEffect(() => {
    let activo = true;

    async function consultarCatalogo() {
      try {
        const [oficinasRespuesta, municipiosRespuesta] =
          await Promise.all([
            oficinasApi.listar({ includeInactive: false }),
            municipiosApi.listar({ includeInactive: false })
          ]);

        const oficinas = ipdListaCatalogo(oficinasRespuesta);
        const municipios = ipdListaCatalogo(municipiosRespuesta);

        const nombresMunicipios = new Map(
          municipios.map(m => [
            String(m.municipioId || m.id || ""),
            m.nombre || ""
          ])
        );

        const nuevas = oficinas
          .filter(ipdHabilitadaInsumos)
          .map(o => {
            const id = String(o.oficinaId || o.id || "");

            return {
              id,
              oficinaId: id,
              oficina: o.nombre || id,
              municipio:
                nombresMunicipios.get(String(o.municipioId || "")) ||
                o.municipio ||
                "",
              impresora: "",
              modelo: "",
              totalImpresoras: null,
              promedioMensual: null,
              stock: {},
              nuevaOficina: true,
              capturaInicialPendiente: true
            };
          });

        if (activo) setOficinasNuevasCatalogo(nuevas);
      } catch (error) {
        console.error("IPD_INSUMOS_CATALOGO_ERROR", error);
      }
    }

    consultarCatalogo();

    return () => {
      activo = false;
    };
  }, []);

  const oficinasParaCaptura = useMemo(() => {
    const actuales = Array.isArray(captureRows) ? captureRows : [];
    const ids = new Set(
      actuales.map(o => String(o.oficinaId || o.id || ""))
    );

    return [
      ...actuales,
      ...oficinasNuevasCatalogo.filter(o => !ids.has(o.oficinaId))
    ];
  }, [captureRows, oficinasNuevasCatalogo]);

  // IP_DEPOT_PATCH02_NOMBRES_V1
  // Los IDs, cantidades y filas originales permanecen intactos.
  const [nombresOficialesStock, setNombresOficialesStock] = useState({});

  useEffect(() => {
    let active = true;

    cargarNombresOficialesStock()
      .then((data) => {
        if (active) setNombresOficialesStock(data);
      })
      .catch((error) => {
        console.warn("[PATCH02] Catalogo no disponible", error);
        if (active) setNombresOficialesStock({});
      });

    return () => {
      active = false;
    };
  }, []);

  function getOficinaOficialStock(row) {
    const id = String(row?.oficinaId || row?.id || "");

    // Alias legacy: SOLO PRESENTACION
    const aliases = {
      BENITO_TAXISTAS: "BJ_TAXISTAS",
      OTHON_BLANCO: "OTHON_P_BLANCO"
    };

    const lookupId = aliases[id] || id;
    return nombresOficialesStock[lookupId] || null;
  }

  function nombreStockOficial(row) {
    return getOficinaOficialStock(row)?.nombre ||
      row?.oficina || row?.nombre || row?.oficinaId || "";
  }

  function municipioStockOficial(row) {
    return getOficinaOficialStock(row)?.municipio ||
      row?.municipio || "";
  }
  
  const [backendLoading, setBackendLoading] = useState(false);
  const [backendError, setBackendError] = useState("");
  const [backendRequisicion, setBackendRequisicion] = useState(null);
  const [backendFolio, setBackendFolio] = useState("");
  const [savingStock, setSavingStock] = useState(false);
  const [generatingRequisition, setGeneratingRequisition] = useState(false);

  async function loadBackendRequisicion() {
    setBackendLoading(true);
    setBackendError("");

    try {
      const data = await getRequisicionActual();

      const allRows = Array.isArray(data?.rows)
        ? data.rows.map(normalizeBackendInsumosRow)
        : [];

      const requestRowsBackend = Array.isArray(data?.requestRows)
        ? data.requestRows.map(normalizeBackendInsumosRow)
        : [];

      setRows(requestRowsBackend);
      setCaptureRows(allRows.length ? allRows : requestRowsBackend);

      setBackendRequisicion({
        ...data,
        rows: allRows,
        requestRows: requestRowsBackend,
      });
    } catch (error) {
      console.error("INSUMOS_BACKEND_LOAD_ERROR", error);
      setBackendError(error.message || "No se pudo consultar el backend de insumos.");
    } finally {
      setBackendLoading(false);
    }
  }

  async function handleStockSaveBackend(nextRows) {
    const normalizedRows = Array.isArray(nextRows)
      ? nextRows.map(row => row.nuevaOficina
          ? row
          : normalizeBackendInsumosRow(row))
      : [];

    // PATCH05D5N: no confirmar stock visual antes de respuesta del backend.
    setSavingStock(true);
    setBackendError("");

    const payload = {
      responsable: "Supervisor operativo",
      captures: buildCapturesFromRows(normalizedRows),
    };

    console.log("INSUMOS_STOCK_SAVE_PAYLOAD", payload);

    try {
      await saveStockCapturas(payload);
      await loadBackendRequisicion();
      return true;
    } catch (error) {
      console.error("INSUMOS_STOCK_SAVE_ERROR", error);
      setBackendError(error.message || "No se pudo guardar la captura de stock.");
      return false;
    } finally {
      setSavingStock(false);
    }
  }

  async function handleCreateBackendRequisition() {
    setGeneratingRequisition(true);
    setBackendError("");

    try {
      const data = await createRequisicionAutomatica();
      const folio = data?.requisicion?.folio || "";
      setBackendFolio(folio);

      await loadBackendRequisicion();

      if (folio) {
        window.alert("Requisición automática generada: " + folio);
      }

      return data;
    } catch (error) {
      console.error("INSUMOS_REQUISITION_CREATE_ERROR", error);
      setBackendError(error.message || "No se pudo generar la requisición automática.");
      return null;
    } finally {
      setGeneratingRequisition(false);
    }
  }

  useEffect(() => {
    loadBackendRequisicion();
  }, []);
const [showStockCapture, setShowStockCapture] = useState(false);
  const [historialModalOpen, setHistorialModalOpen] = useState(false);
  const [historialOficina, setHistorialOficina] = useState(null);

  function abrirHistorial(row) {
    setHistorialOficina({
      oficinaId: row.oficinaId || row.id,
      label: nombreStockOficial(row) + " · " + municipioStockOficial(row),
    });
    setHistorialModalOpen(true);
  }

  /* INSUMOS_STOCK_OFFICE_VIEW_V1 */
  const [activeModuleView, setActiveModuleView] = useState("requisition");
  const [stockSearch, setStockSearch] = useState("");
  const [stockPrinterFilter, setStockPrinterFilter] = useState("");
  const [stockStatusFilter, setStockStatusFilter] = useState("");
  const [stockDisplayMode, setStockDisplayMode] = useState("table");

  const calculatedRows = useMemo(function() {
    return rows.slice().sort(function(a, b) {
      const s = severity(b.estado) - severity(a.estado);
      if (s !== 0) return s;
      return String(a.oficina || "").localeCompare(String(b.oficina || ""));
    });
  }, [rows]);

  const operationalStockRows = useMemo(function() {
    const baseRows =
      captureRows.length
        ? captureRows
        : backendRequisicion?.rows || [];

    return baseRows
      .map(function(row) {
        const normalized = normalizeBackendInsumosRow(row);
        const calculated = calcularOficina(normalized);

        let stockVisualStatus = "NORMAL";

        if (calculated.estado === "VENCIDA") {
          stockVisualStatus = "CRITICO";
        } else if (calculated.estado === "HOY") {
          stockVisualStatus = "PEDIR_HOY";
        } else if (calculated.estado === "PREVENTIVA") {
          stockVisualStatus = "PREVENTIVO";
        }

        return {
          ...calculated,
          stockVisualStatus,
        };
      })
      .sort(function(a, b) {
        const statusOrder = {
          CRITICO: 4,
          PEDIR_HOY: 3,
          PREVENTIVO: 2,
          NORMAL: 1,
        };

        const statusDiff =
          statusOrder[b.stockVisualStatus] -
          statusOrder[a.stockVisualStatus];

        if (statusDiff !== 0) return statusDiff;

        return String(a.oficina || "").localeCompare(
          String(b.oficina || ""),
          "es",
          { sensitivity: "base" }
        );
      });
  }, [captureRows, backendRequisicion]);

  const filteredOperationalStockRows = useMemo(function() {
    const search = normalizeText(stockSearch);

    return operationalStockRows.filter(function(row) {
      const searchable = normalizeText(
        [
          row.oficina,
          row.municipio,
          row.printerLabel,
          labelInsumo(row.insumoLimitante),
        ].join(" ")
      );

      if (search && !searchable.includes(search)) {
        return false;
      }

      if (
        stockPrinterFilter &&
        row.impresora !== stockPrinterFilter
      ) {
        return false;
      }

      if (
        stockStatusFilter &&
        row.stockVisualStatus !== stockStatusFilter
      ) {
        return false;
      }

      return true;
    });
  }, [
    operationalStockRows,
    stockSearch,
    stockPrinterFilter,
    stockStatusFilter,
  ]);

  const operationalStockTotals = useMemo(function() {
    return operationalStockRows.reduce(
      function(acc, row) {
        const stock = row.stock || {};

        acc.oficinas += 1;
        acc.plasticos += Number(stock.plasticos || 0);
        acc.ribbon += Number(stock.ribbon || 0);
        acc.laminador += Number(stock.laminador || 0);
        acc.retransfer += Number(stock.retransfer || 0);
        acc.rollos += Number(stock.rollos || 0);
        acc.capacidad += Number(row.capacidadReal || 0);

        if (row.stockVisualStatus === "CRITICO") {
          acc.criticas += 1;
        }

        return acc;
      },
      {
        oficinas: 0,
        criticas: 0,
        plasticos: 0,
        ribbon: 0,
        laminador: 0,
        retransfer: 0,
        rollos: 0,
        capacidad: 0,
      }
    );
  }, [operationalStockRows]);

  useEffect(function() {
    if (activeModuleView !== "stock") return undefined;

    const intervalId = window.setInterval(function() {
      loadBackendRequisicion();
    }, 60000);

    return function() {
      window.clearInterval(intervalId);
    };
  }, [activeModuleView]);

  const groups = useMemo(function() {
    return {
      vencidas: calculatedRows.filter(function(row) { return row.estado === "VENCIDA"; }),
      hoy: calculatedRows.filter(function(row) { return row.estado === "HOY"; }),
      preventivas: calculatedRows.filter(function(row) { return row.estado === "PREVENTIVA"; }),
      ok: calculatedRows.filter(function(row) { return row.estado === "OK"; }),
    };
  }, [calculatedRows]);

  const requestRows = useMemo(function() {
    return calculatedRows;
  }, [calculatedRows]);

  const totals = useMemo(function() {
    const backendTotals = backendRequisicion?.totals;

    if (backendTotals) {
      return {
        oficinas: Number(backendTotals.oficinas || 0),
        plasticos: Number(backendTotals.plasticos || 0),
        ribbon: Number(backendTotals.ribbon || 0),
        laminador: Number(backendTotals.laminador || 0),
        retransfer: Number(backendTotals.retransfer || 0),
        rollos: Number(backendTotals.rollos || 0),
      };
    }

    return requestRows.reduce(function(acc, row) {
      const pedido = row.pedidoSugerido || row.pedido || {};

      acc.oficinas += 1;
      acc.plasticos += Number(pedido.plasticos || 0);
      acc.ribbon += Number(pedido.ribbon || 0);
      acc.laminador += Number(pedido.laminador || 0);
      acc.retransfer += Number(pedido.retransfer || 0);
      acc.rollos += Number(pedido.rollos || 0);

      return acc;
    }, { oficinas: 0, plasticos: 0, ribbon: 0, laminador: 0, retransfer: 0, rollos: 0 });
  }, [backendRequisicion, requestRows]);

  function updateOfficeStock(oficinaId, field, value) {
    setCaptureRows(function(current) {
      return current.map(function(row) {
        const rowId = row.oficinaId || row.id;
        if (rowId !== oficinaId) return row;

        return {
          ...row,
          stock: {
            ...(row.stock || {}),
            [field]: numberOrZeroPreserveZero(value),
          },
        };
      });
    });
  }

  function updateOfficeAverage(oficinaId, value) {
    setCaptureRows(function(current) {
      return current.map(function(row) {
        const rowId = row.oficinaId || row.id;
        if (rowId !== oficinaId) return row;

        return {
          ...row,
          promedioMensual: numberOrZeroPreserveZero(value),
        };
      });
    });
  }

  async function generarPdfProveedor() {
    await exportInsumosProveedorPdf({
      requestRows: requestRows.map((row) => ({
        ...row,
        oficina: nombreStockOficial(row),
      })),
      totals: totals,
      leadTimeDays: LEAD_TIME_DAYS,
      safetyPercent: 0,
      folio: backendFolio || backendRequisicion?.folio || backendRequisicion?.requisicion?.folio || "",
      traceability: backendRequisicion?.traceability || {},
    });
  }

  function RequisitionRow(props) {
    const row = props.row;
    const variant = props.variant || "normal";
    const kitLines = pedidoCompactoLineas(row);

    return (
      <div className={"req-row req-row-compact " + variant}>
        <div className="office-col compact-office">
          <span className={estadoVisualClass(row, variant)}>{estadoVisualLabel(row, variant)}</span>
          <div>
            <strong>{nombreStockOficial(row)}</strong>
            {/* IP_DEPOT_PATCH03_MUNICIPIO_V1 */}
            <small>
              {municipioStockOficial(row)}
              {" · "}
              {row.printerLabel || getOperationalPrinterLabel(row)}
            </small>
          </div>
        </div>

        <div className="state-col compact-state">
          <strong>{causaVisualCompacta(row, variant)}</strong>
          <small>{subcausaVisualCompacta(row, variant)}</small>
        </div>

        <div className="qty-col compact-kit">
          <div className="compact-kit-head">
            <strong>{kitMetaCompacta(row)}</strong>
          </div>

          <div className="compact-kit-pills">
            {kitLines.length === 0 ? (
              <span className="compact-pill empty">Sin pedido</span>
            ) : kitLines.map(function(item) {
              return (
                <span className={"compact-pill compact-" + item.key} key={item.key}>
                  <em>{item.label}</em>
                  <b>{fmt(item.value)}</b>
                </span>
              );
            })}
          </div>
        </div>

        <div className="action-col compact-action">
          <div className={"auto-result-status compact-status " + (isComplementaria(row, variant) ? "compact-status-complementaria" : "")}>
            <strong>{accionVisualTitulo(row, variant)}</strong>
            <small>{accionVisualSubtitulo(row, variant)}</small>
          </div>
        </div>
      </div>
    );
  }

  if (activeModuleView === "stock") {
    return (
      <div className="logistica-page stock-office-page">
          {/* IPD_PATCH05D4B - oficinas pendientes en vista Stock */}
          <PendientesCatalogoInsumos />

        <main className="main">
          <header className="top stock-office-header">
            <div>
              <span className="stock-office-kicker">
                CONTROL OPERATIVO
              </span>

              <h1>Stock operativo por oficina</h1>

              <p>
                Existencias capturadas, capacidad efectiva y material
                limitante por oficina.
              </p>
            </div>

            <div className="top-actions">
            {!isProvider && (<a href="/acuses-control" className="stock-view-secondary-btn" style={{textDecoration:"none",display:"inline-flex",alignItems:"center"}}>Control de acuses</a>)}
              <button
                className="stock-view-secondary-btn"
                type="button"
                onClick={() =>
                  setActiveModuleView("requisition")
                }
              >
                Ver requisición automática
              </button>

              {!isProvider && (
                <button
                  className="stock-view-secondary-btn"
                  type="button"
                  disabled={backendLoading}
                  onClick={loadBackendRequisicion}
                >
                  {backendLoading
                    ? "Actualizando..."
                    : "Actualizar stock"}
                </button>
              )}

              {!isProvider && (
                <button
                  className="stock-btn"
                  type="button"
                  onClick={() => setShowStockCapture(true)}
                >
                  Capturar stock operativo
                </button>
              )}
            </div>
          </header>

          <section className="stock-office-kpis">
            <article>
              <span>Oficinas</span>
              <strong>
                {fmt(operationalStockTotals.oficinas)}
              </strong>
              <small>Con stock registrado</small>
            </article>

            <article className="stock-kpi-critical">
              <span>Críticas</span>
              <strong>
                {fmt(operationalStockTotals.criticas)}
              </strong>
              <small>Requieren atención</small>
            </article>

            <article>
              <span>Tarjetas disponibles</span>
              <strong>
                {fmt(operationalStockTotals.plasticos)}
              </strong>
              <small>Unidades físicas</small>
            </article>

            <article>
              <span>Capacidad efectiva estatal</span>
              <strong>
                {fmt(operationalStockTotals.capacidad)}
              </strong>
              <small>Licencias estimadas</small>
            </article>

            <article>
              <span>Última actualización</span>
              <strong className="stock-kpi-date">
                {backendRequisicion?.traceability?.ultimaCaptura
                  ? new Date(
                      backendRequisicion.traceability.ultimaCaptura
                    ).toLocaleString("es-MX")
                  : "Sin fecha"}
              </strong>
              <small>
                Actualización automática cada 60 segundos
              </small>
            </article>
          </section>

          {backendError && (
            <div className="stock-office-error">
              {backendError}
            </div>
          )}

          <section className="stock-office-controls">
            <label className="stock-office-search">
              <span>Buscar oficina</span>
              <input
                type="search"
                placeholder="Oficina, municipio o material..."
                value={stockSearch}
                onChange={(event) =>
                  setStockSearch(event.target.value)
                }
              />
            </label>

            <label>
              <span>Modelo de impresora</span>
              <select
                value={stockPrinterFilter}
                onChange={(event) =>
                  setStockPrinterFilter(event.target.value)
                }
              >
                <option value="">Todos</option>
                <option value="IDP_SMART_51L">
                  SMART 51L
                </option>
                <option value="K30_SWITF_PRO">
                  K30 SWITF PRO
                </option>
              </select>
            </label>

            <label>
              <span>Estado operativo</span>
              <select
                value={stockStatusFilter}
                onChange={(event) =>
                  setStockStatusFilter(event.target.value)
                }
              >
                <option value="">Todos</option>
                <option value="CRITICO">Crítico</option>
                <option value="PEDIR_HOY">Pedir hoy</option>
                <option value="PREVENTIVO">Preventivo</option>
                <option value="NORMAL">Operación normal</option>
              </select>
            </label>

            <div className="stock-office-view-switch">
              <button
                type="button"
                className={
                  stockDisplayMode === "table"
                    ? "active"
                    : ""
                }
                onClick={() => setStockDisplayMode("table")}
              >
                Tabla
              </button>

              <button
                type="button"
                className={
                  stockDisplayMode === "cards"
                    ? "active"
                    : ""
                }
                onClick={() => setStockDisplayMode("cards")}
              >
                Tarjetas
              </button>
            </div>
          </section>

          <div className="stock-office-result-count">
            Mostrando{" "}
            <strong>
              {filteredOperationalStockRows.length}
            </strong>{" "}
            de{" "}
            <strong>{operationalStockRows.length}</strong>{" "}
            oficinas
          </div>

          {stockDisplayMode === "table" ? (
            <section className="stock-office-table-card">
              <div className="stock-office-table-wrap">
                <table className="stock-office-table">
                  <thead>
                    <tr>
                      <th>Oficina / modelo</th>
                      <th>Tarjetas</th>
                      <th>Ribbon</th>
                      <th>Laminador</th>
                      <th>Retransfer</th>
                      <th>Rollos</th>
                      <th>Capacidad efectiva</th>
                      <th>Cobertura</th>
                      <th>Limitante</th>
                      <th>Estado</th>
                      <th>Historial</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredOperationalStockRows.map(
                      function(row) {
                        const stock = row.stock || {};

                        return (
                          <tr key={row.oficinaId || row.id}>
                            <td>
                              <strong>{nombreStockOficial(row)}</strong>
                              <span>{municipioStockOficial(row)}</span>
                              <small>
                                {row.printerLabel}
                              </small>
                            </td>

                            <td>
                              <b>{fmt(stock.plasticos)}</b>
                              <small>
                                {fmt(row.capacidades.plasticos)} lic.
                              </small>
                            </td>

                            <td>
                              <b>{fmt(stock.ribbon)}</b>
                              <small>
                                {fmt(row.capacidades.ribbon)} lic.
                              </small>
                            </td>

                            <td>
                              <b>{fmt(stock.laminador)}</b>
                              <small>
                                {fmt(row.capacidades.laminador)} lic.
                              </small>
                            </td>

                            <td>
                              {row.reglas.requiereRetransfer ? (
                                <>
                                  <b>
                                    {fmt(stock.retransfer)}
                                  </b>
                                  <small>
                                    {fmt(
                                      row.capacidades.retransfer
                                    )}{" "}
                                    lic.
                                  </small>
                                </>
                              ) : (
                                <span className="stock-na">
                                  No aplica
                                </span>
                              )}
                            </td>

                            <td>
                              <b>{fmt(stock.rollos)}</b>
                              <small>
                                Mínimo{" "}
                                {fmt(
                                  row.reglas.minRollosTerminal
                                )}
                              </small>
                            </td>

                            <td className="stock-capacity-cell">
                              <strong>
                                {fmt(row.capacidadReal)}
                              </strong>
                              <span>licencias</span>
                            </td>

                            <td>
                              <b>{dec(row.diasCobertura)} días</b>
                              <small>
                                Promedio{" "}
                                {fmt(row.promedioMensual)}/mes
                              </small>
                            </td>

                            <td>
                              <strong>
                                {labelInsumo(
                                  row.insumoLimitante
                                )}
                              </strong>
                            </td>

                            <td>
                              <span
                                className={
                                  "stock-office-status status-" +
                                  row.stockVisualStatus.toLowerCase()
                                }
                              >
                                {row.stockVisualStatus === "CRITICO"
                                  ? "CRÍTICO"
                                  : row.stockVisualStatus ===
                                    "PEDIR_HOY"
                                  ? "PEDIR HOY"
                                  : row.stockVisualStatus ===
                                    "PREVENTIVO"
                                  ? "PREVENTIVO"
                                  : "NORMAL"}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="stock-view-secondary-btn"
                                onClick={() => abrirHistorial(row)}
                              >
                                Ver histórico
                              </button>
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <section className="stock-office-card-grid">
              {filteredOperationalStockRows.map(function(row) {
                const stock = row.stock || {};

                return (
                  <article
                    className="stock-office-card"
                    key={row.oficinaId || row.id}
                  >
                    <header>
                      <div>
                        <strong>{nombreStockOficial(row)}</strong>
                        <span>{municipioStockOficial(row)}</span>
                      </div>

                      <small>{row.printerLabel}</small>
                    </header>

                    <div className="stock-office-card-materials">
                      <div>
                        <span>Tarjetas</span>
                        <strong>{fmt(stock.plasticos)}</strong>
                        <small>
                          {fmt(row.capacidades.plasticos)} lic.
                        </small>
                      </div>

                      <div>
                        <span>Ribbon</span>
                        <strong>{fmt(stock.ribbon)}</strong>
                        <small>
                          {fmt(row.capacidades.ribbon)} lic.
                        </small>
                      </div>

                      <div>
                        <span>Laminador</span>
                        <strong>{fmt(stock.laminador)}</strong>
                        <small>
                          {fmt(row.capacidades.laminador)} lic.
                        </small>
                      </div>

                      <div>
                        <span>Retransfer</span>
                        <strong>
                          {row.reglas.requiereRetransfer
                            ? fmt(stock.retransfer)
                            : "N/A"}
                        </strong>
                        <small>
                          {row.reglas.requiereRetransfer
                            ? fmt(
                                row.capacidades.retransfer
                              ) + " lic."
                            : "No aplica"}
                        </small>
                      </div>

                      <div>
                        <span>Rollos</span>
                        <strong>{fmt(stock.rollos)}</strong>
                        <small>
                          Mínimo{" "}
                          {fmt(row.reglas.minRollosTerminal)}
                        </small>
                      </div>
                    </div>

                    <div className="stock-office-card-result">
                      <div>
                        <span>Capacidad efectiva</span>
                        <strong>
                          {fmt(row.capacidadReal)} licencias
                        </strong>
                      </div>

                      <div>
                        <span>Material limitante</span>
                        <strong>
                          {labelInsumo(row.insumoLimitante)}
                        </strong>
                      </div>

                      <div>
                        <span>Cobertura</span>
                        <strong>
                          {dec(row.diasCobertura)} días
                        </strong>
                      </div>
                    </div>

                    <footer>
                      <span
                        className={
                          "stock-office-status status-" +
                          row.stockVisualStatus.toLowerCase()
                        }
                      >
                        {row.stockVisualStatus === "CRITICO"
                          ? "CRÍTICO"
                          : row.stockVisualStatus ===
                            "PEDIR_HOY"
                          ? "PEDIR HOY"
                          : row.stockVisualStatus ===
                            "PREVENTIVO"
                          ? "PREVENTIVO"
                          : "OPERACIÓN NORMAL"}
                      </span>
                      <button
                        type="button"
                        className="stock-view-secondary-btn"
                        onClick={() => abrirHistorial(row)}
                      >
                        Ver histórico
                      </button>
                    </footer>
                  </article>
                );
              })}
            </section>
          )}

          <StockCaptureModal
            ultimaCapturaInventario={backendRequisicion?.traceability?.ultimaCaptura ?? null}
            savingStock={savingStock}
            open={showStockCapture}
            rows={oficinasParaCaptura.length ? oficinasParaCaptura : calculatedRows}
            onClose={() => setShowStockCapture(false)}
            onUpdateStock={updateOfficeStock}
            onUpdateAverage={updateOfficeAverage}
            onSave={handleStockSaveBackend}
          />

          <StockHistorialModal
            open={historialModalOpen}
            onClose={() => setHistorialModalOpen(false)}
            oficinaId={historialOficina?.oficinaId}
            oficinaLabel={historialOficina?.label}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="logistica-page">
      <main className="main">
        <header className="top">
          <div>
            <h1>Requisición Insumos para Licencias de Conducir</h1>
            <p>Requisición generada automáticamente según última captura de stock</p>
          </div>

          <div className="top-actions">
            {!isProvider && (<a href="/acuses-control" className="stock-view-secondary-btn" style={{textDecoration:"none",display:"inline-flex",alignItems:"center"}}>Control de acuses</a>)}
            <button
              className="stock-view-secondary-btn"
              type="button"
              onClick={() => setActiveModuleView("stock")}
            >
              Visualizar stock por oficina
            </button>

            {!isProvider && (
              <button className="stock-btn" type="button" onClick={() => setShowStockCapture(true)}>
                Capturar stock operativo
              </button>
            )}
            {!isProvider && (
              <button className="pdf-btn" type="button" onClick={generarPdfProveedor}>
                ▣ Generar PDF proveedor
              </button>
            )}
          </div>
        </header>

        <section className="summary-strip">
          <div className="legend">
            <span><i className="dot red" /> {fmt(groups.vencidas.length)} críticas</span>
            <span><i className="dot amber" /> {fmt(groups.hoy.length)} pedir hoy</span>
            <span><i className="dot green" /> {fmt(groups.preventivas.length)} preventivas</span>
          </div>

          <div className="total-line">
            Total consolidado: <strong>{fmt(totals.plasticos + totals.ribbon + totals.laminador + totals.retransfer + Number(totals.rollos || 0))} unidades</strong>
          </div>
        </section>

<section className="stock-traceability">
          <div className="stock-traceability-main">
            <span>TRAZABILIDAD DE STOCK</span>
            <strong>Datos usados para calcular esta requisición</strong>
          </div>

          <div className="stock-traceability-grid">
            <article>
              <span>Última captura</span>
              <strong>{backendRequisicion?.traceability?.ultimaCaptura ? new Date(backendRequisicion.traceability.ultimaCaptura).toLocaleString("es-MX") : STOCK_TRACEABILITY.ultimaCaptura}</strong>
            </article>

            <article>
              <span>Responsable</span>
              <strong>{backendRequisicion?.traceability?.responsable || STOCK_TRACEABILITY.responsable}</strong>
            </article>

            <article>
              <span>Oficinas actualizadas</span>
              <strong>{backendRequisicion?.traceability?.oficinasActualizadas ?? STOCK_TRACEABILITY.oficinasActualizadas} / {backendRequisicion?.traceability?.oficinasTotales ?? STOCK_TRACEABILITY.oficinasTotales}</strong>
            </article>

            <article className={(backendRequisicion?.traceability?.pendientes ?? (STOCK_TRACEABILITY.oficinasTotales - STOCK_TRACEABILITY.oficinasActualizadas)) > 0 ? "trace-warning" : "trace-ok"}>
              <span>Pendientes</span>
              <strong>{backendRequisicion?.traceability?.pendientes ?? (STOCK_TRACEABILITY.oficinasTotales - STOCK_TRACEABILITY.oficinasActualizadas)}</strong>
            </article>
          </div>
        </section>

        <section className="auto-rule-notice">
          <strong>Requisición automática</strong>
          
        </section>

        <div className="content">
          <section className="work">
            <section className="req-section vencidas">
              <header>
                <h2> A. CRÍTICAS / VENCIDAS</h2>
                <span>Incluidas automáticamente por riesgo crítico</span>
              </header>

              <div className="req-table-head">
                <span>Oficina / Modelo</span>
                <span>Causa</span>
                <span>Pedido sugerido</span>
                <span>PDF</span>
              </div>

              {groups.vencidas.map(function(row) {
                return <RequisitionRow key={row.oficinaId} row={row} />;
              })}
            </section>

            <section className="req-section hoy">
              <header>
                <h2> B. PEDIR HOY</h2>
              </header>

              <div className="req-table-head">
                <span>Oficina / Modelo</span>
                <span>Causa</span>
                <span>Pedido sugerido</span>
                <span>PDF</span>
              </div>

              {groups.hoy.length === 0 ? (
                <div className="req-empty">Sin oficinas en punto de pedido hoy.</div>
              ) : groups.hoy.map(function(row) {
                return <RequisitionRow key={row.oficinaId} row={row} variant="today" />;
              })}
            </section>

            <section className="req-section preventivas">
              <header>
                <h2> C. PREVENTIVAS</h2>
              </header>

              <div className="req-table-head">
                <span>Oficina / Modelo</span>
                <span>Causa</span>
                <span>Pedido sugerido</span>
                <span>PDF</span>
              </div>

              {groups.preventivas.length === 0 ? (
                <div className="req-empty">Sin oficinas preventivas.</div>
              ) : groups.preventivas.map(function(row) {
                return <RequisitionRow key={row.oficinaId} row={row} variant="preventive" />;
              })}
            </section>
            <section className="req-section kit">
              <header>
                <h2> D. COMPLEMENTARIAS</h2>
                <span>No críticas; incluidas por kit cerrado o compra consolidada</span>
              </header>

              <div className="req-table-head">
                <span>Oficina / Modelo</span>
                <span>Causa</span>
                <span>Pedido sugerido</span>
                <span>PDF</span>
              </div>

              {groups.ok.filter(tienePedidoProveedor).length === 0 ? (
                <div className="req-empty">Sin oficinas complementarias.</div>
              ) : groups.ok.filter(tienePedidoProveedor).map(function(row) {
                return <RequisitionRow key={row.oficinaId} row={row} variant="kit" />;
              })}
            </section>

          </section>

          <aside className="right">
            <section className="doc-card">
              <div className="doc-card-title">
                <span>PDF PROVEEDOR</span>
                <h3>Listo para generar</h3>
                <p>Documento consolidado por stock capturado</p>
              </div>

              <div className="included">
                <span></span>
                <strong>Oficinas incluidas en PDF</strong>
                <b>{fmt(totals.oficinas)}</b>
              </div>

              <div className="included-breakdown">
                <span><b>{fmt(groups.vencidas.length)}</b> críticas</span>
                <span><b>{fmt(groups.preventivas.length)}</b> preventivas</span>
                <span><b>{fmt(groups.ok.filter(tienePedidoProveedor).length)}</b> complementarias</span>
              </div>

              <h4>Unidades por insumo</h4>

              <div className="bar-row pvc">
                <div><span>Plásticos (PVC)</span><strong>{fmt(totals.plasticos)}</strong></div>
                <i />
              </div>

              <div className="bar-row ribbon">
                <div><span>Ribbon YMCKO</span><strong>{fmt(totals.ribbon)}</strong></div>
                <i />
              </div>

              <div className="bar-row laminador">
                <div><span>Laminador 10mil</span><strong>{fmt(totals.laminador)}</strong></div>
                <i />
              </div>

              <div className="bar-row retransfer">
                <div><span>Retransfer Film</span><strong>{fmt(totals.retransfer)}</strong></div>
                <i />
              </div>

              <div className="bar-row rollos">
                <div><span>Rollo térmico terminal</span><strong>{fmt(totals.rollos)}</strong></div>
                <i />
              </div>

              <small className="note">
                * Requisición automática. El cálculo justifica cada material usando promedio mensual, cobertura esperada,
                lead time de {LEAD_TIME_DAYS} días, stock actual capturado y cuello de botella por oficina.
              </small>

              {!isProvider && (
                <button
                  type="button"
                  disabled={generatingRequisition}
                  onClick={async () => {
                    const result = await handleCreateBackendRequisition();

                    if (result) {
                      generarPdfProveedor();
                    }
                  }}
                >
                  {generatingRequisition ? "GENERANDO..." : "GENERAR DOCUMENTO AUTOMÁTICO"}
                </button>
              )}
            </section>

            <section className="criteria">
              <h3> Criterio de cálculo</h3>
              <p>Actualizado: 12:45 PM</p>
              <ul>
                <li>Vencidas: Prioridad crítica por fecha límite rebasada</li>
                <li>Colchón operativo: stock actual capturado</li>
                <li>Lead Time Proveedor: {LEAD_TIME_DAYS} días hábiles</li>
              </ul>
            </section>
          
          <section className="insumos-side-card insumos-equivalencias-card">
            <div className="insumos-side-card-title">
              <span>Equivalencias operativas</span>
              <small>Referencia de cálculo por modelo</small>
            </div>

            <div className="insumos-equivalencias-list">
              {INSUMOS_EQUIVALENCIAS_OPERATIVAS.map((grupo) => (
                <article className="insumos-equivalencia-grupo" key={grupo.modelo}>
                  <h4>{grupo.modelo}</h4>

                  {grupo.items.map((item) => (
                    <div className="insumos-equivalencia-row" key={`${grupo.modelo}-${item.insumo}`}>
                      <strong>{item.insumo}</strong>
                      <span>{item.equivalencia}</span>
                    </div>
                  ))}
                </article>
              ))}

              <article className="insumos-equivalencia-grupo insumos-equivalencia-rollos">
                <h4>Todos</h4>
                <div className="insumos-equivalencia-row">
                  <strong>Rollo térmico terminal</strong>
                  <span>Mínimo operativo: 17 rollos</span>
                </div>
              </article>
            </div>

            <p className="insumos-equivalencias-note">
              Nota: una licencia impresa consume doble laminado, equivalente a 2 laminaciones.
            </p>
          </section>
</aside>
        </div>


        <StockCaptureModal
            ultimaCapturaInventario={backendRequisicion?.traceability?.ultimaCaptura ?? null} savingStock={savingStock}
          open={showStockCapture}
          rows={oficinasParaCaptura.length ? oficinasParaCaptura : calculatedRows}
          onClose={() => setShowStockCapture(false)}
          onUpdateStock={updateOfficeStock}
          onUpdateAverage={updateOfficeAverage}
          onSave={handleStockSaveBackend}
        />

        
      </main>
    </div>
  );
}
















